import React, { useEffect, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FunctionsIcon from '@mui/icons-material/Functions';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TagIcon from '@mui/icons-material/Tag';
import {
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { LensCard, LensColumnKind } from '~/types/leLens';

import { governedDisplayField, useGovernedDisplayMetadata } from '../governedDisplayMetadata';

import { fieldBrowserUsedNames } from './fieldBrowserFields';
import {
  type FieldBrowserFilter,
  type FieldBrowserPreferences,
  readFieldBrowserPreferences,
  writeFieldBrowserPreferences,
} from './fieldBrowserPreferences';
import { cardsUsingField, countFieldUsage } from './fieldUsage';
import {
  PANEL_FIELD_GROUP_BY_KIND,
  PANEL_FIELD_GROUP_ORDER,
  type PanelField,
  type PanelFieldGroup,
  SHELF_LABELS,
  type ShelfKey,
} from './panelTypes';
import type { PanelState } from './usePanelState';

/** Kind override targets: a manual correction to a declared column's
 *  inferred kind (00_PLAN.md: "The author can override kind per column"),
 *  never 'calculated' - that is a structural fact of having a formula, not
 *  a label a plain declared column can be given. */
const KIND_OVERRIDE_OPTIONS: Array<{ value: Exclude<LensColumnKind, 'calculated'>; label: string }> = [
  { value: 'date', label: 'Date' },
  { value: 'dimension', label: 'Dimension' },
  { value: 'measure', label: 'Measure' },
];

const KIND_ICON: Record<LensColumnKind, React.ElementType> = {
  calculated: FunctionsIcon,
  date: CalendarMonthOutlinedIcon,
  dimension: SellOutlinedIcon,
  measure: TagIcon,
};

const KIND_NAME: Record<LensColumnKind, string> = {
  calculated: 'calculated',
  date: 'date',
  dimension: 'dimension',
  measure: 'measure',
};

/** Case-insensitive match over name, label and description. Empty search matches everything. */
export function matchesFieldSearch(field: PanelField, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return [field.name, field.label, field.description].some((value) => value?.toLowerCase().includes(needle));
}

/** Groups fields Dates, Dimensions, Measures, Calculated; a group is omitted entirely once it has
 *  nothing left to show, per "show nothing the reader does not need at rest". */
export function groupFields(fields: ReadonlyArray<PanelField>): Array<[PanelFieldGroup, Array<PanelField>]> {
  return PANEL_FIELD_GROUP_ORDER.map((group) => [
    group,
    fields.filter((field) => PANEL_FIELD_GROUP_BY_KIND[field.kind] === group),
  ]).filter(([, items]) => items.length > 0) as Array<[PanelFieldGroup, Array<PanelField>]>;
}

/** Which shelf (if any) on the current card already holds this field, read
 *  entirely from the PanelState surface Shelves/ShelfChip already use. */
function shelfHolding(state: PanelState, fieldName: string): ShelfKey | undefined {
  return state.shelfKeys.find((key) => state.shelfValues(key).includes(fieldName));
}

/** The one line shown under the kind picker: what happens elsewhere on the
 *  card if this field stops being its current kind. Exported for a direct
 *  unit test rather than only through the rendered row. */
export function kindChangeConsequence(field: PanelField, shelf: ShelfKey | undefined): string {
  if (!shelf) {
    return 'Not used on this card yet.';
  }
  if (shelf === 'values') {
    return 'Table columns accept any kind.';
  }
  if (shelf === 'value') {
    return `Removes it from ${SHELF_LABELS.value}; pick a different field to total.`;
  }
  return `Removes it from ${SHELF_LABELS[shelf]}; a numeric aggregate will be needed.`;
}

function toggleGroupCollapse(preferences: FieldBrowserPreferences, group: PanelFieldGroup): FieldBrowserPreferences {
  const collapsed = preferences.collapsed.includes(group)
    ? preferences.collapsed.filter((entry) => entry !== group)
    : [...preferences.collapsed, group];
  return { ...preferences, collapsed };
}

export default function FieldList({
  cards,
  disabled,
  loading,
  onChangeFieldKind,
  onFieldHover,
  onMenuOpenChange,
  onRelabelField,
  onRemoveField,
  scrollMode = 'internal',
  state,
}: {
  cards: ReadonlyArray<LensCard>;
  disabled?: boolean;
  loading?: boolean;
  onChangeFieldKind?: (field: PanelField, kind: Exclude<LensColumnKind, 'calculated'>) => void;
  onFieldHover?: (hover: { datasetId: string; column: string; cardIds: Array<string> } | null) => void;
  /** Lets the panel hold Escape from closing while this row menu, or an
   *  inline relabel/kind editor it opened, is open. */
  onMenuOpenChange?: (open: boolean) => void;
  onRelabelField?: (field: PanelField, label: string) => void;
  onRemoveField?: (field: PanelField) => void;
  scrollMode?: 'internal' | 'page';
  state: PanelState;
}) {
  const tokens = lensTokens(useTheme());
  const datasetId = state.draft.datasetId;
  const displayMetadata = useGovernedDisplayMetadata();
  const displayField = (field: PanelField) => governedDisplayField(field, state.dataset, displayMetadata);
  const [preferences, setPreferences] = useState(() => readFieldBrowserPreferences(datasetId));
  useEffect(() => {
    const restored = readFieldBrowserPreferences(datasetId);
    setPreferences(restored);
    state.setSearch(restored.search);
    // Restore this dataset's browsing position without changing chart assignments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);
  const remember = (next: FieldBrowserPreferences) => {
    setPreferences(next);
    writeFieldBrowserPreferences(datasetId, next);
  };
  const used = fieldBrowserUsedNames(state.draft.view);
  const usedCount = state.fields.filter((field) => used.has(field.name)).length;
  const pinnedCount = state.fields.filter((field) => preferences.pinned.includes(field.name)).length;
  const matchesFilter = (field: PanelField): boolean => {
    if (preferences.filter === 'used') {
      return used.has(field.name);
    }
    if (preferences.filter === 'pinned') {
      return preferences.pinned.includes(field.name);
    }
    return true;
  };
  const searching = Boolean(state.search.trim());
  const filtered = state.fields.filter(
    (field) => matchesFieldSearch(displayField(field), state.search) && matchesFilter(field),
  );
  const groups = groupFields(filtered);
  const fieldColumns = [
    groups.filter(([group]) => group === 'Dates' || group === 'Dimensions'),
    groups.filter(([group]) => group === 'Measures' || group === 'Calculated'),
  ];
  const togglePinned = (field: PanelField) =>
    remember({
      ...preferences,
      pinned: preferences.pinned.includes(field.name)
        ? preferences.pinned.filter((name) => name !== field.name)
        : [...preferences.pinned, field.name],
    });
  const [menu, setMenu] = useState<{ anchor: HTMLElement; field: PanelField } | null>(null);
  // Which row is mid-edit: "Relabel" still opens an inline text input in the
  // row's own place; a field's own kind glyph opens the segmented kind
  // picker directly beneath the row, no menu click first.
  const [editing, setEditing] = useState<{ field: PanelField; mode: 'relabel' | 'kind' } | null>(null);
  const [labelDraft, setLabelDraft] = useState('');
  const [tooltipField, setTooltipField] = useState<string | null>(null);
  const [dismissedTooltip, setDismissedTooltip] = useState<string | null>(null);

  useEffect(() => {
    onMenuOpenChange?.(Boolean(menu) || Boolean(editing));
    // onMenuOpenChange is a hand-off, not a dependency: only the menu or an
    // inline editor opening or closing should notify the panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, editing]);

  const startRelabel = (field: PanelField) => {
    setLabelDraft(field.label ?? '');
    setEditing({ field, mode: 'relabel' });
  };

  const commitRelabel = () => {
    if (editing) {
      onRelabelField?.(editing.field, labelDraft);
    }
    setEditing(null);
  };

  const toggleKindEditor = (field: PanelField) => {
    setEditing((current) =>
      current?.mode === 'kind' && current.field.name === field.name ? null : { field, mode: 'kind' },
    );
  };

  if (loading) {
    return (
      <Stack spacing={1}>
        <Skeleton height={36} variant="rounded" />
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton height={32} key={index} variant="rounded" />
        ))}
      </Stack>
    );
  }

  const emptyMessage = searching
    ? 'No fields match'
    : preferences.filter === 'pinned'
      ? "No pinned fields yet. Pin one from a field's menu."
      : preferences.filter === 'used'
        ? 'No fields in this chart yet.'
        : 'No fields to show.';

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: scrollMode === 'page' ? 'auto' : '100%', minHeight: 0 }}
    >
      <Stack
        alignItems="center"
        direction="row"
        spacing={1}
        sx={{
          bgcolor: 'background.paper',
          flexShrink: 0,
          position: scrollMode === 'page' ? 'sticky' : 'static',
          top: 0,
          zIndex: 1,
        }}
      >
        <TextField
          disabled={disabled}
          fullWidth
          inputProps={{ 'aria-label': 'Search fields' }}
          onChange={(event) => {
            state.setSearch(event.target.value);
            remember({ ...preferences, search: event.target.value });
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: state.search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear field search"
                  onClick={() => {
                    state.setSearch('');
                    remember({ ...preferences, search: '' });
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          placeholder="Search fields"
          size="small"
          value={state.search}
        />
        <ToggleButtonGroup
          disabled={disabled}
          exclusive
          onChange={(_event, value: Exclude<FieldBrowserFilter, 'all'> | null) =>
            remember({ ...preferences, filter: value ?? 'all' })
          }
          size="small"
          value={preferences.filter === 'all' ? null : preferences.filter}
        >
          <ToggleButton aria-label="Show only fields used in this chart" sx={{ height: 32, width: 32 }} value="used">
            <Tooltip title={`Show only fields used in this chart · ${usedCount}`}>
              <CheckCircleOutlineIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton aria-label="Show only pinned fields" sx={{ height: 32, width: 32 }} value="pinned">
            <Tooltip title={`Show only pinned fields · ${pinnedCount}`}>
              <StarOutlineIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {state.fields.length === 0 && (
        <Typography color="text.secondary" sx={{ display: 'block', mt: 1.5 }} variant="caption">
          {datasetId ? 'No columns declared yet.' : 'Pick a dataset to see its fields.'}
        </Typography>
      )}
      {state.fields.length > 0 && groups.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }} variant="body2">
          {emptyMessage}
        </Typography>
      )}
      {/* The consolidated Data pane scrolls as one page. Other hosts may keep
          the field library in its own scroll region. */}
      <Box
        onScroll={() => setTooltipField(null)}
        sx={{
          flex: scrollMode === 'page' ? 'none' : 1,
          minHeight: 0,
          mt: 0.5,
          overflowY: scrollMode === 'page' ? 'visible' : 'auto',
          scrollbarGutter: scrollMode === 'page' ? 'auto' : 'stable',
          pr: 1,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            minHeight: scrollMode === 'page' ? 0 : '100%',
          }}
        >
          {fieldColumns.map((columnGroups, columnIndex) => (
            <Stack
              key={columnIndex === 0 ? 'descriptive-fields' : 'measure-fields'}
              spacing={0}
              sx={{
                borderLeft: columnIndex === 1 ? 1 : 0,
                borderColor: 'divider',
                minWidth: 0,
                pl: columnIndex === 1 ? 1.5 : 0,
                pr: columnIndex === 0 ? 1.5 : 0,
              }}
            >
              {columnGroups.map(([group, items]) => {
                const collapsed = !searching && preferences.collapsed.includes(group);
                return (
                  <Box key={group} sx={{ mt: 0.5 }}>
                    <Box
                      aria-expanded={!collapsed}
                      component="button"
                      onClick={() => remember(toggleGroupCollapse(preferences, group))}
                      sx={{
                        alignItems: 'center',
                        background: 'none',
                        border: 0,
                        color: 'inherit',
                        cursor: 'pointer',
                        display: 'flex',
                        font: 'inherit',
                        gap: 0.5,
                        minHeight: 32,
                        p: 0,
                        textAlign: 'left',
                        width: '100%',
                      }}
                      type="button"
                    >
                      <ExpandMoreIcon
                        sx={{
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                          color: 'text.secondary',
                          fontSize: 16,
                          transform: collapsed ? 'rotate(-90deg)' : 'none',
                          transition: 'transform 150ms',
                        }}
                      />
                      <Typography
                        color="text.secondary"
                        sx={{ flex: 1, fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
                      >
                        {group}
                      </Typography>
                      <Typography color="text.secondary" variant="caption">
                        {items.length}
                      </Typography>
                    </Box>
                    {!collapsed && (
                      <Stack spacing={0}>
                        {items.map((field) => {
                          const presented = displayField(field);
                          const Glyph = KIND_ICON[field.kind];
                          const usedBy = datasetId ? countFieldUsage(datasetId, field.name, cards) : 0;
                          const isUsed = used.has(field.name);
                          const isEditingRelabel = editing?.mode === 'relabel' && editing.field.name === field.name;
                          const isEditingKind = editing?.mode === 'kind' && editing.field.name === field.name;
                          const isMenuOpenForField = menu?.field.name === field.name;

                          if (isEditingRelabel) {
                            return (
                              <Stack key={field.name} spacing={0} sx={{ py: 0.25 }}>
                                <Stack alignItems="center" direction="row" spacing={0.25}>
                                  <TextField
                                    autoFocus
                                    fullWidth
                                    inputProps={{ 'aria-label': `Display name for ${field.name}` }}
                                    onChange={(event) => setLabelDraft(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        commitRelabel();
                                      }
                                      if (event.key === 'Escape') {
                                        setEditing(null);
                                      }
                                    }}
                                    placeholder={field.name}
                                    size="small"
                                    value={labelDraft}
                                  />
                                  <IconButton
                                    aria-label={`Save display name for ${field.name}`}
                                    onClick={commitRelabel}
                                    size="small"
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    aria-label={`Cancel renaming ${field.name}`}
                                    onClick={() => setEditing(null)}
                                    size="small"
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                                <Typography color="text.secondary" sx={{ display: 'block', pl: 0.5 }} variant="caption">
                                  Saves immediately, not with the card&rsquo;s Save button.
                                </Typography>
                              </Stack>
                            );
                          }

                          const descriptionText =
                            presented.description ?? (field.kind === 'calculated' ? field.formula : undefined);
                          const usedByText =
                            usedBy > 0 ? `Used by ${usedBy} card${usedBy === 1 ? '' : 's'}` : undefined;
                          const hasTooltip = Boolean(descriptionText || usedByText);

                          const row = (
                            <Box
                              key={field.name}
                              onPointerDownCapture={() => {
                                setTooltipField(null);
                                setDismissedTooltip(field.name);
                              }}
                              onDragStartCapture={() => {
                                setTooltipField(null);
                                setDismissedTooltip(field.name);
                              }}
                              onMouseLeave={() => {
                                setTooltipField(null);
                                setDismissedTooltip(null);
                              }}
                              sx={{ borderRadius: 1 }}
                            >
                              <Box
                                onMouseEnter={() => {
                                  if (!datasetId) {
                                    return;
                                  }
                                  const cardIds = cardsUsingField(datasetId, field.name, cards).map((card) => card.id);
                                  onFieldHover?.({ cardIds, column: field.name, datasetId });
                                }}
                                onMouseLeave={() => onFieldHover?.(null)}
                                sx={{
                                  alignItems: 'center',
                                  borderRadius: 1,
                                  display: 'flex',
                                  gap: 0.25,
                                  minHeight: 32,
                                  '&:hover': disabled ? undefined : { bgcolor: alpha(tokens.accent, 0.08) },
                                  '&:hover .le-lens-field-kebab, &:focus-within .le-lens-field-kebab': { opacity: 1 },
                                }}
                              >
                                <IconButton
                                  aria-expanded={isEditingKind}
                                  aria-label={`${field.name} is a ${KIND_NAME[field.kind]}. Change kind`}
                                  disabled={disabled || field.kind === 'calculated'}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    toggleKindEditor(field);
                                  }}
                                  size="small"
                                  sx={{
                                    color: isUsed || isEditingKind ? tokens.accent : 'text.secondary',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Glyph sx={{ fontSize: 16 }} />
                                </IconButton>
                                {/* A native button cannot contain the menu IconButton below, so the
                              assignable, draggable, keyboard-focusable part is its own button
                              and the row menu trigger is a sibling, not a nested control. */}
                                <Box
                                  aria-label={`Field ${field.name}`}
                                  component="button"
                                  disabled={disabled}
                                  draggable={!disabled}
                                  onClick={() => state.assign(field)}
                                  onDragStart={(event) => event.dataTransfer.setData('text/plain', field.name)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' && event.shiftKey) {
                                      event.preventDefault();
                                      setMenu({ anchor: event.currentTarget, field });
                                      return;
                                    }
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      state.assign(field);
                                    }
                                  }}
                                  sx={{
                                    alignItems: 'center',
                                    background: 'none',
                                    border: 0,
                                    borderRadius: 1,
                                    color: 'inherit',
                                    cursor: disabled ? 'default' : 'pointer',
                                    display: 'flex',
                                    flex: 1,
                                    font: 'inherit',
                                    gap: 1,
                                    minWidth: 0,
                                    px: 0.5,
                                    py: 0.5,
                                    textAlign: 'left',
                                  }}
                                  type="button"
                                >
                                  <Typography
                                    noWrap
                                    sx={{ flex: 1, fontWeight: isUsed ? 500 : 400, minWidth: 0 }}
                                    title={presented.label || field.name}
                                    variant="body2"
                                  >
                                    {presented.label || field.name}
                                  </Typography>
                                  {preferences.pinned.includes(field.name) && (
                                    <StarIcon sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 12 }} />
                                  )}
                                  {isUsed && <CheckIcon sx={{ color: tokens.accent, flexShrink: 0, fontSize: 14 }} />}
                                </Box>
                                <IconButton
                                  aria-label={`${field.name} field menu`}
                                  className="le-lens-field-kebab"
                                  disabled={disabled}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setMenu({ anchor: event.currentTarget, field });
                                  }}
                                  size="small"
                                  sx={{
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                    flexShrink: 0,
                                    height: 24,
                                    opacity: isMenuOpenForField ? 1 : 0,
                                    transition: 'opacity 120ms',
                                    width: 24,
                                  }}
                                >
                                  <MoreVertIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                              {isEditingKind && (
                                <Box sx={{ pb: 1, pl: 4.5, pr: 1, pt: 0.5 }}>
                                  <ToggleButtonGroup
                                    aria-label={`Kind for ${field.name}`}
                                    exclusive
                                    fullWidth
                                    onChange={(_event, value: Exclude<LensColumnKind, 'calculated'> | null) => {
                                      if (value) {
                                        onChangeFieldKind?.(field, value);
                                        setEditing(null);
                                      }
                                    }}
                                    size="small"
                                    value={field.kind === 'calculated' ? null : field.kind}
                                  >
                                    {KIND_OVERRIDE_OPTIONS.map((option) => (
                                      <ToggleButton
                                        key={option.value}
                                        sx={{ textTransform: 'none' }}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </ToggleButton>
                                    ))}
                                  </ToggleButtonGroup>
                                  <Typography
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                    variant="caption"
                                  >
                                    {kindChangeConsequence(field, shelfHolding(state, field.name))} Saves immediately,
                                    not with the card&rsquo;s Save button.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          );
                          return hasTooltip ? (
                            <Tooltip
                              key={field.name}
                              title={
                                <>
                                  {descriptionText}
                                  {descriptionText && usedByText ? <br /> : null}
                                  {usedByText}
                                </>
                              }
                              describeChild
                              disableInteractive
                              disableTouchListener
                              enterDelay={650}
                              enterNextDelay={650}
                              leaveDelay={0}
                              placement="left"
                              open={tooltipField === field.name && dismissedTooltip !== field.name && !menu && !editing}
                              onOpen={() => setTooltipField(field.name)}
                              onClose={() => setTooltipField(null)}
                              slotProps={{ popper: { sx: { pointerEvents: 'none' } } }}
                            >
                              {row}
                            </Tooltip>
                          ) : (
                            row
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          ))}
        </Box>
      </Box>

      <Menu anchorEl={menu?.anchor} onClose={() => setMenu(null)} open={Boolean(menu)}>
        <MenuItem
          onClick={() => {
            if (menu) {
              togglePinned(menu.field);
            }
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <StarOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {menu && preferences.pinned.includes(menu.field.name) ? 'Unpin field' : 'Pin field'}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) {
              startRelabel(menu.field);
            }
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Relabel</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menu) {
              void navigator.clipboard?.writeText(menu.field.name)?.catch?.(() => undefined);
            }
            setMenu(null);
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy name</ListItemText>
        </MenuItem>
        {menu?.field.kind === 'calculated' && (
          <MenuItem
            onClick={() => {
              if (menu) {
                onRemoveField?.(menu.field);
              }
              setMenu(null);
            }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Remove</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
